/**
 * Voice handler for `navigate_back` — equivalent to clicking the
 * browser back arrow / the in-app back arrow at the top-left of a
 * page. Mounted globally from NokiCopilot.jsx.
 *
 * State-aware via the click path: the existing in-app leave-guards
 * (Confirmation modals, unsaved-draft prompts on the recording-
 * screen, etc) fire automatically when React Router pops history.
 * Voice doesn't implement state-dependent branching here — that's
 * Delta-D-second-cut work (declarative `post_actions` on
 * ToolDefinition) and lands in a follow-up. Today: `navigate(-1)` is
 * sufficient because the existing UI guards already cover the
 * recording-active and unsaved-changes cases.
 *
 * Server precondition `not_in_recording_transition` blocks the call
 * mid-stop / mid-draft-save so the agent never pulls the rug out
 * from under an in-flight transition.
 *
 * Capability matrix:
 *
 *   #  Voice                              Behaviour
 *   1  "go back"                          navigate(-1)
 *   2  "back"                             same
 *   3  "previous page"                    same
 *   4  "return to where I was"            same
 *   5  (no history)                       caught by browser; no-op
 *   6  (recording active)                 leave-guard prompt fires
 *   7  (transition in flight)             server: blocked / busy
 *
 * Failure modes:
 *   - blocked_by_state: not_in_recording_transition — server-side
 *   - host_exception: useNavigate not wired (developer error)
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useCopilotAction } from "./actionRegistry";

const DESCRIPTOR = Object.freeze({
  name: "navigate_back",
  description:
    "Implemented by noki-fe (react-router-v6 navigate(-1)).",
  classification: "READ",
  parameters: [],
});

export function useCopilotNavigateBackAction() {
  const navigate = useNavigate();
  const descriptor = useMemo(() => DESCRIPTOR, []);

  useCopilotAction(descriptor, async () => {
    // eslint-disable-next-line no-console
    console.info("[copilot] navigate_back.invoke");
    if (typeof navigate !== "function") {
      throw new Error("host_exception: navigate not wired.");
    }
    navigate(-1);
    return { navigated: true, direction: "back" };
  });
}
